import React from 'react'
import { Button } from '@demandlocal/components'
import toast from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'

const Terms = ({
  onClose,
  submitButtonLabel,
}: {
  onClose?: () => void
  submitButtonLabel?: string
}): JSX.Element => {
  const navigate = useNavigate()
  const { search } = useLocation()

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onSubmit',
  })

  const onSubmit = (): void => {
    try {
      if (onClose) {
        onClose()
      } else {
        const urlSearchParams = new URLSearchParams(search)
        const nextUrl = urlSearchParams.get('next')
        if (nextUrl) navigate(nextUrl)
        else navigate('/dashboard')
      }
    } catch (err) {
      toast.error(
        `There was an issue accepting the terms at this point in time. Try again or contact your representative`,
        { duration: 5000 },
      )
    }
  }

  return (
    <div className="bg-black/30 flex justify-center items-center h-[100vh]">
      <div className="flex flex-col p-5 w-[840px] rounded-md overflow-scroll-y bg-white gap-2">
        <div className="flex flex-row justify-between items-center">
          <h1 className="font-Spartan font-bold text-[28px]">Terms of Service</h1>
          <span className="text-xs">Effective October 3, 2023</span>
        </div>
        <div className="flex flex-col gap-3 overflow-y-scroll max-h-[80vh] p-3">
          <span className="text-xl border-b-2 border-gainsboro-gray pb-3">
            These LinkOne Terms of Service (this “Agreement”) constitute a binding
            agreement between you, as an individual to the extent you're accessing the
            Service in your individual capacity, or the organization that you represent
            (“you”, “your” or “Customer”) to include all individuals you authorize to
            access your account, and governs your use of Demand Local, Inc.'s (“Demand
            Local”) Services (as defined below). By signing up to use the Services, you
            agree to these Terms and to Demand Local's Privacy Policy, which is available
            at link1data.com. Customer and Demand Local are referred to herein
            individually as a “Party” and collectively as the “Parties”. Capitalized terms
            used but not defined shall have the meanings ascribed to such terms in the
            Standard Terms (as defined below). Demand Local has developed a proprietary
            software-as-a-service tool (the “Platform”) to assist users in creating and
            managing digital marketing campaigns and certain other media offerings
            (collectively, “Campaigns”)
          </span>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">1. Services</span>
              <span>
                1.1. <span className="underline">Services</span>. As used herein, the term
                “<span className="font-bold">Services</span>” shall be include (i) the
                Platform, (ii) the Campaigns, and (iii) the Additional Services (as
                defined below).
              </span>
              <span>
                1.2. <span className="underline">License</span>. Subject to the terms and
                conditions hereof, Demand Local hereby grants to Customer a limited,
                non-transferable, and non-exclusive license (the “
                <span className="font-bold">License</span>”) to use the Platform during
                the term hereof solely in accordance with the terms of this Agreement and
                any specifications, instructions, and documentation (collectively, the “
                <span className="font-bold">Documentation</span>”) provided by Demand
                Local from time to time. The License shall be subject to the terms and
                conditions of one or more Campaigns (the “
                <span className="font-bold">Campaign Terms</span>”) created by Customer
                through the Platform. Customer shall use the Platform and the
                Documentation solely for its own internal business purposes and in
                accordance with the limitations, if any, set forth in the Campaign Terms.
              </span>
              <span>
                1.3. <span className="underline">Campaign Terms</span>. Customer may
                create and manage Campaigns on the Platform. The Campaign and all related
                services will be provided to Customer on a calendar-month basis (each such
                monthly term, a “<span className="font-bold">Monthly Term</span>”).
                Campaign Terms may vary for each Campaign. A Campaign may span more than
                one Monthly Term. Neither Party has a contractual obligation to perform a
                Campaign unless and until Customer creates the Campaign on the Platform.
                Unless explicitly stated otherwise in the applicable Campaign Terms, a
                Campaign will begin on the effective date and will automatically renew at
                the close of each Monthly Term for successive Monthly Terms unless and
                until terminated by either Party (as provided in Section 11.2).
              </span>
              <span>
                1.4.{' '}
                <span className="underline">Delivering of Advertising Materials</span>.
                Customer will submit all Advertising Materials (including any requested
                changes to previously-submitted Advertising Materials) to Demand Local at
                least forty- eight (48) business hours in advance of the requested
                placement date. All Advertising Materials will reference the applicable
                Campaign and the requested placement date. Demand Local will not issue any
                credits or provide any makegoods to Customer for Demand Local's failure to
                meet the requested placement date unless Customer fully complies with this
                Section 1.5.
              </span>
              <span>
                1.5. <span className="underline">Modifications to the platform</span>.
                Demand Local may modify and/or update the Platform from time to time, so
                long as such modification(s) do not materially reduce the Platform's
                performance or capabilities. Demand Local shall have no liability for any
                damage, liabilities, losses (including any loss of data or profits), or
                any other consequences that Customer, any of Customer's authorized
                employees and personnel who are authorized to access the Platform and
                Documentation (“
                <span className="font-bold">Authorized Users</span>”), or any other third
                party may incur as a result of modifications to the Platform in accordance
                with this Section 1.2.
              </span>
              <span>
                1.6. <span className="underline">Additional Services</span>. During the
                term hereof, Demand Local may agree to provide Customer with such
                additional services as agreed to by the Parties (the “
                <span className="font-bold">Additional Services</span>”). Except as
                specified herein or in an applicable Campaign Terms, nothing in this
                Agreement shall be construed as a guarantee of future Services outside the
                scope of an applicable Campaign Terms.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">2. Campaigns</span>
              <span>
                {' '}
                2.1. <span className="underline">Binding Agreement</span>. By creating a
                Campaign on the Platform, Customer understands and agrees that Customer is
                bound to the Campaign Terms, including, without limitation, executing and
                paying for the applicable Campaign.
              </span>
              <span>
                2.2. <span className="underline">Agreements Governing Campaigns</span>.
                ampaigns shall be governed by this Agreement and the corresponding
                Campaign Terms. In addition, and except as provided herein, the Parties
                agree that the Services shall be governed by the IAB 4As Standard Terms
                and Conditions Version 3.0 (located at
                http://www.iab.net/media/file/IAB_4As-tsandcs-FINAL.pdf) (the “
                <span className="font-bold">Standard Terms</span>”). Notwithstanding any
                terms to the contrary in the Standard Terms, the Parties understand and
                agree that, to the extent anything in this Agreement conflicts or is
                inconsistent with the Standard Terms, this Agreement shall control and
                supersede such conflicting or inconsistent terms in the Standard Terms
                unless it is expressly noted herein that such terms supersede any
                conflicting or inconsistent terms in this Agreement. Further, the Parties
                agree that the Standard Terms apply to media buys of one year or less. The
                Standard terms do not apply to sponsorships and other arrangements
                involving content association or integration or special production, or any
                other relationship other than media buys of one year or less, which shall
                the require a separate agreement between all relevant parties.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">3. Financial Terms</span>
              <span>
                3.1. <span className="underline">Fees</span>. In consideration for the
                grant of the License and the provision of the Services, Customer shall pay
                to Demand Local the fees set forth in the Campaign Terms (the “
                <span className="font-bold">Fees</span>”) in accordance with this Section
                3.
              </span>
              <span>
                3.2. <span className="underline">Expenses</span>. If Customer's use of the
                Services incurs third-party expenses (the, “
                <span className="font-bold">Third Party Fees</span>”), Customer agrees
                that Demand Local may, at its discretion, either pay the Third Party Fees
                on Customer's behalf or pass along such Third Party Fees to be paid
                directly by Customer. If Demand Local pays Third Party Fees on Customer's
                behalf, Demand Local shall invoice Customer for such Third Party Fees and
                such fees shall be payable in accordance with Section 3.3
              </span>
              <span>
                3.3. <span className="underline">Invoices</span>. Unless otherwise
                indicated in an applicable Campaign Terms, Demand Local will invoice
                Customer for all Services performed during a Monthly Term within thirty
                (30) days of the end of such Monthly Term (though failure by Demand Local
                to invoice Customer during such period shall not release Customer of its
                obligation to pay such invoice), and payment of such invoice shall be due
                and payable within thirty (30) days of the date of the invoice. Invoices
                may be satisfied via an automatic charge to the credit card Customer keeps
                on file in Customer's online account with the Services. Customer hereby
                agrees that Demand Local may charge any such credit card in Customer's
                account for any Fees incurred by the Customer. Customer is obligated to
                pay all invoiced amounts regardless of whether any proceeds, sums, or
                payment from Advertiser have cleared to, or otherwise been received by,
                Customer, whether for Ads placed by Customer or otherwise.
              </span>
              <span>
                3.4. <span className="underline">Method of Payment</span>. Unless Demand
                Local states otherwise in writing, all amounts due and payable hereunder
                shall be paid (a) in U.S. Dollars, and (b) by credit/debit card via an
                authorized Demand Local payment processor or by any other method approved
                in writing by Demand Local.
              </span>
              <span>
                3.5. <span className="underline">Interest and Taxes</span>. Interest on
                any late payments will accrue at the rate of 1.5% per month, or the
                highest rate permitted by applicable law, whichever is lower, from the
                date such amount is due until the date such amount is paid in full.
                Customer will also pay all costs and expenses of collection incurred by
                Demand Local, including reasonable attorneys' fees and expenses. In no
                event will any limitation of liability in the Standard Terms or otherwise
                apply to Customer's obligations or liability under this Section. Customer
                acknowledges that Demand Local's performance of its obligations under this
                Agreement is expressly contingent on Customer's timely payment of invoiced
                amounts. Customer will be responsible for, and will pay all sales and
                similar taxes, and all similar fees levied upon the provision of the
                Services, excluding only taxes based solely on Demand Local's income.
                Customer will indemnify and hold Demand Local harmless from and against
                any and all such taxes and related amounts levied upon the provision of
                the Services and any costs associated with the collection or withholding
                thereof, including penalties and interest.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">
                4. Customer Restrictions and Responsibilities.
              </span>
              <span>
                4.1. <span className="underline">Restrictions on Use of Platform</span>.
                Except as expressly authorized by this Agreement, Customer may not: (a)
                modify, disclose, alter, translate or create derivative works of the
                Platform or the Documentation (or any components of the foregoing); (b)
                sublicense, resell, distribute, lease, rent, lend, transfer, assign or
                otherwise dispose of the Platform or the Documentation (or any components
                of the foregoing); (c) reverse engineer, disassemble, decompile, decode,
                adapt, or otherwise attempt to derive or gain access to any source code,
                object code, or underlying structure, ideas, or algorithms of the
                Platform, in whole or in part; (d) use the Platform to store or transmit
                any viruses, software routines or other code designed to permit
                unauthorized access, to disable, erase or otherwise harm software,
                hardware or data, or to perform any other harmful actions; (e) use the
                Platform or Documentation in any manner or for any purpose that infringes,
                misappropriates, or otherwise violates any intellectual property right or
                other right of any person, or that violates any applicable Laws; (f)
                interfere with or disable any features, functionality, or security
                controls of the Platform or otherwise circumvent any protection mechanisms
                for the Platform; (g) copy, frame or mirror any part or content of the
                Platform; (h) build a competitive product or service, or copy any features
                or functions of the Platform; (i) interfere with or disrupt the integrity
                or performance of the Platform; (j) attempt to gain unauthorized access to
                the Platform or related systems or networks; (k) disclose to any third
                party any performance information or analysis relating to the Platform;
                (l) use the components of the Platform or allow the transfer,
                transmission, export or re-export of such software components or any
                portion thereof in violation of any export control Laws administered by
                the U.S. Commerce Department, OFAC, or any other government agency; (m)
                remove, alter or obscure any proprietary notices in or on the Platform,
                including any copyright notices, or (n) cause its personnel or any third
                party to do any of the foregoing. Customer will use its best efforts to
                prevent unauthorized access to, and use of, any passwords, and will
                immediately notify Demand Local in writing of any unauthorized use that
                comes to Customer's attention. Notwithstanding anything to the contrary in
                this Agreement, Demand Local may temporarily suspend or permanently revoke
                Customer's access to the Service if Demand Local determines or reasonably
                suspects that Customer has or intends to violate, or has assisted others
                in violating or preparing to violate, any provision of this Section 3 (any
                such temporary suspension, a “Service Suspension” and any such revocation,
                a “Service Revocation”). Demand Local shall have no liability for any
                damage, liabilities, losses (including any loss of data or profits), or
                any other consequences that Customer or any third party may incur as a
                result of a Service Suspension or Service Revocation, and Customer shall
                not be entitled to any refunds of any Fees on account of any Service
                Suspension or Service Revocation. Any breach by Customer of any provision
                of this Section 4 shall be an incurable material breach and will entitle
                Demand Local to terminate this Agreement and any Campaigns immediately
                pursuant to Section 11.2(a).
              </span>
              <span>
                4.2. <span className="underline"> Customer Responsibilities</span>.
                Customer shall be solely responsible for: (a) obtaining and maintaining
                any equipment and ancillary services needed to connect to, access or
                otherwise use the Platform and the Services; (b) maintaining the security
                of Customer's infrastructure, equipment, accounts, passwords (including
                but not limited to administrative and user passwords) and files; (c)
                providing Demand Local and its personnel with support and system access
                needed to perform the Services; and (d) all acts and omissions of
                Authorized Users in connection with their use of the Platform and
                Services.
              </span>
              <span>
                4.3. <span className="underline">Age Policy</span>. The Service is not
                intended to be used by any person under 18 years of age. By using the
                Service, you represent to Demand Local that you are at least 18 years old
                and are legally able to enter into this Agreement. Demand Local does not
                knowingly collect or solicit personally identifiable information from
                anyone under 18; if you are under 18, please do not use or attempt to use
                the Services or send any personal information about yourself to Demand
                Local. If Demand Local learns it have collected personal information from
                anyone under 18, Demand Local will delete that information.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">5. Confidentiality</span>
              <span>
                5.1. <span className="underline">Definition</span>. “
                <span className="font-bold">Confidential Information</span>” means all
                information disclosed (whether in oral, written, or other tangible or
                intangible form) by one Party (the “
                <span className="font-bold">Disclosing Party</span>”) to the other Party
                (the “<span className="font-bold">Receiving Party</span>”) concerning or
                related to this Agreement or the Disclosing Party (whether before, on, or
                after the Effective Date) that is marked “Confidential” or “Proprietary”
                or with similar designation by the Disclosing Party, or that otherwise
                should reasonably be deemed to be confidential based on the context and
                nature of the information. Confidential Information includes, but is not
                limited to, computer programs in source and/or object code, technical
                drawings, algorithms, know-how, prototypes, models, samples, formulas,
                processes, ideas, inventions (whether patentable or not), discoveries,
                methods, strategies and techniques, research, development, design details
                and specifications, financial information, procurement and/or purchasing
                requirements, customer lists, information about investors, employees,
                business or contractual relationships, sales and merchandising data,
                business forecasts and marketing plans, and similar information.
              </span>
              <span>
                5.2. <span className="underline">Obligations</span>. The Receiving Party
                shall maintain in confidence the Confidential Information during the term
                of this Agreement and for a period of two (2) years thereafter, and will
                not use such Confidential Information except as expressly permitted in
                this Agreement; provided, however, that any trade secrets shall be treated
                confidentially for so long as such information qualifies for protection as
                trade secret under applicable law. The Receiving Party will use the same
                degree of care in protecting the Confidential Information as the Receiving
                Party uses to protect its own confidential and proprietary information
                from unauthorized use or disclosure, but in no event less than reasonable
                care. Confidential Information will be used by the Receiving Party solely
                for the purpose of carrying out the Receiving Party's obligations under
                this Agreement. In addition, the Receiving Party will only disclose
                Confidential Information to its directors, officers, employees and/or
                contractors who have a need to know such Confidential Information in order
                to perform their duties under this Agreement, and only if such directors,
                officers, employees and/or contractors are bound by confidentiality
                obligations with respect to such Confidential Information no less
                restrictive than the non-disclosure obligations contained in this Section
                5.2. The Parties agree that Customer Data (as defined below) shall be
                considered Customer's Confidential Information and the terms and
                conditions of this Agreement will be treated as Confidential Information
                of both Parties and will not be disclosed to any third party; provided,
                however, that each Party may disclose the terms and conditions of this
                Agreement (a) to such Party's legal counsel, accountants, banks, financing
                sources and their advisors, (b) in connection with the enforcement of this
                Agreement or rights under this Agreement, or (c) in connection with an
                actual or proposed merger, acquisition, or similar transaction.
              </span>
              <span>
                5.3. <span className="underline">Exceptions</span>. Notwithstanding
                anything to the contrary herein, Confidential Information will not include
                information that: (a) is in or enters the public domain without breach of
                this Agreement and through no fault of the Receiving Party; (b) the
                Receiving Party can reasonably demonstrate was in its possession prior to
                first receiving it from the Disclosing Party; (c) the Receiving Party can
                demonstrate was developed by the Receiving Party independently, and
                without use of or reference to, the Confidential Information; or (d) the
                Receiving Party receives from a third party without restriction on
                disclosure and without breach of a nondisclosure obligation. In addition,
                the Receiving Party may disclose Confidential Information that is required
                to be disclosed by applicable Laws or by a subpoena or order issued by a
                court of competent jurisdiction or other governmental authority (each, an
                “<span className="font-bold">Order</span>”), but solely on the conditions
                that the Receiving Party, to the extent permitted by applicable Laws: (i)
                gives the Disclosing Party written notice of the Order promptly after
                receiving it; and (ii) cooperates fully with the Disclosing Party before
                disclosure to provide the Disclosing Party with the opportunity to
                interpose any objections it may have to the disclosure of the information
                required by the Order and seek a protective order or other appropriate
                relief. In the event of any dispute between the Parties as to whether
                specific information is within one or more of the exceptions set forth in
                this Section 4.3, the Receiving Party will bear the burden of proof, by
                clear and convincing evidence, that such information is within the claimed
                exception(s).
              </span>
              <span>
                5.4. <span className="underline">Remedies</span>. The Receiving Party
                acknowledges that any unauthorized disclosure of Confidential Information
                will result in irreparable injury to the Disclosing Party, which injury
                could not be adequately compensated by the payment of money damages. In
                addition to any other legal and equitable remedies that may be available,
                the Disclosing Party will be entitled to seek and obtain injunctive relief
                against any breach or threatened breach by the Receiving Party of the
                confidentiality obligations hereunder, from any court of competent
                jurisdiction, without being required to show any actual damage or
                irreparable harm, prove the inadequacy of its legal remedies, or post any
                bond or other security.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">6. Intellectual Property Rights</span>
              <span>
                6.1. <span className="underline">Generally</span>. Except as specified in
                Section 6.3, no provision of this Agreement shall be construed as an
                assignment or transfer of ownership of any copyrights, patents, trade
                secrets, trademarks, or any other intellectual property rights
                (collectively, “
                <span className="font-bold">Intellectual Property Rights</span>”) from
                either Party to the other.
              </span>
              <span>
                6.2. <span className="underline">Platform and Services</span>. Demand
                Local shall own and retain all right, title and interest in and to: (a)
                the name, logo, trademarks, and service marks (collectively, “
                <span className="font-bold">Marks</span>”) associated with its business;
                (b) the Platform and the Documentation; (c) all improvements, enhancements
                and modifications to the Platform and the Documentation; (d) any work
                products or deliverables resulting from any Services provided to Customer
                under this Agreement (except for any work products or deliverables that is
                expressly set forth in an applicable Campaign Terms to be owned by
                Customer); and (e) all Intellectual Property Rights related to any of the
                foregoing. Demand Local reserves all rights in and to the Platform and the
                Documentation not expressly granted to Customer in this Agreement. Except
                for the rights and licenses expressly granted in this Agreement, nothing
                in this Agreement grants to Customer or any third party, by implication,
                waiver, estoppel, or otherwise, any right, title, or interest in or to the
                Platform or the Documentation.
              </span>
              <span>
                6.3. <span className="underline">Feedback</span>. If Customer or any of
                its Authorized Users submits written suggestions or recommended changes to
                the Platform or Services, including without limitation, new features or
                functionality relating thereto, or any comments, questions, suggestions,
                or the like (collectively, the “
                <span className="font-bold">Feedback</span>”), Demand Local is free to use
                such Feedback regardless of any other obligation or limitation between the
                Parties governing such Feedback. Customer hereby assigns to Demand Local,
                on Customer's behalf and on behalf of its Authorized Users and/or agents,
                all Intellectual Property Rights in and to the Feedback, for any purpose
                whatsoever, although Demand Local is not required to use any Feedback.
              </span>
              <span>
                6.3. <span className="underline">Use of Customer Marks</span>. Customer
                shall own and retain all right, title and interest in and to the Marks
                relating to Customer's business and all Intellectual Property Rights
                related thereto.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">7. Data</span>
              <span>
                7.1. <span className="underline">Ownership of Cutomer Data</span>. All
                Customer Data (as defined below), including any Customer Data input into
                the Platform by Customer or generated through Customer's use of the
                Platform, shall belong to Customer, provided that Demand Local shall have
                the right to access, use, and process such Customer Data to provide the
                Services and the functionality of the Platform to Customer during the term
                of this Agreement. Customer acknowledges and agrees that Customer is
                solely responsible for any and all Customer Data that is input into the
                Platform by Customer, including such Customer Data's legality,
                reliability, and appropriateness. As used herein, “
                <span className="font-bold">Customer Data</span>” means data uploaded by
                Customer or resulting from Customer's use of the Platform, including
                Personal Data, Employee Personal Data and Third Party Personal Data (as
                such terms are defined below), as well as Advertising Materials.
              </span>
              <span>
                7.2. <span className="underline">Anonymized Data</span>. Customer
                acknowledges and agrees that Demand Local may anonymize and aggregate
                Customer Data in a manner that it can no longer reasonably be used to
                identify individuals (“<span className="font-bold">Anonymized Data</span>
                ”). Customer grants Demand Local and its affiliates, an unlimited,
                perpetual, and irrevocable license to use the Anonymized Data for the
                purpose of improving the Platform and Services, to understand and analyze
                trends across Demand Local's customers, and for any other purpose.
              </span>
              <span>
                7.3. <span className="underline">Employee and Third Party Data</span>.
                Customer acknowledges that, as part of its use of the Platform, Customer
                may transmit their own personal data (“
                <span className="font-bold">Personal Data</span>”) or the personal data of
                its employees or other personnel (“
                <span className="font-bold">Employee Personal Data</span>”) and personal
                data of third parties (“
                <span className="font-bold">Third Party Personal Data</span>”). To the
                extent Customer transmits or inputs any Personal Data, Employee Personal
                Data or Third Party Personal Data through or into the Platform, Customer
                represents and warrants that it has obtained all legally required consent
                to capture, collect, display, input, share and transmit such Personal
                Data, Employee Personal Data and Third Party Personal Data into and
                through the Platform.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">8. Representations, Warranties, Remedies</span>
              <span>
                8.1. <span className="underline">Generally</span>. Each Party represents
                and warrants that (a) it is validly existing and in good standing under
                the Laws of the place of its establishment or incorporation, (b) it has
                full corporate power and authority to execute, deliver and perform its
                obligations under this Agreement, (c) the person signing this Agreement on
                its behalf has been duly authorized and empowered to enter into this
                Agreement, (d) this Agreement is valid, binding and enforceable against it
                in accordance with its terms, except to the extent limited under Laws
                relating to insolvency, bankruptcy, and the like, and (e) it is not under
                any pre-existing obligation in conflict or in any way inconsistent with
                the provisions of this Agreement.
              </span>
              <span>
                8.2.{' '}
                <span className="underline">
                  Demand Local's Representations and Warranties
                </span>
                . Demand Local represents and warrants that (a) the Platform will conform,
                in all material respects, to the Documentation, and (b) it will perform
                the Services, if any, in a professional and workmanlike manner.
              </span>
              <span>
                8.3.{' '}
                <span className="underline">
                  Customer's Representations and Warranties
                </span>
                . Customer represents and warrants that Customer: (a) will use the
                Platform and the Services only in compliance with this Agreement and all
                applicable local, state, federal and international laws and regulations,
                rules, orders, and ordinances (collectively, “
                <span className="font-bold">Laws</span>”); (b) shall not infringe upon any
                third party's Intellectual Property Rights in its use of the Platform and
                the Services; and (c) has obtained and will obtain and continue to have,
                during the Term, all necessary rights, authority and licenses for the
                access to and use of the Company Data (including any Personal Data,
                Employee Personal Data and Third Party Personal Data) as contemplated by
                this Agreement.
              </span>
              <span>
                8.4. <span className="underline">Disclaimer</span>. THE PLATFORM AND
                SERVICES ARE PROVIDED “AS-IS” AND “AS- AVAILABLE.” EXCEPT AS EXPRESSLY SET
                FORTH IN THIS AGREEMENT, DEMAND LOCAL DISCLAIMS ANY AND ALL
                REPRESENTATIONS OR WARRANTIES (EXPRESS OR IMPLIED, ORAL OR WRITTEN) WITH
                RESPECT TO THE PLATFORM AND THE SERVICES PROVIDED UNDER THIS AGREEMENT,
                WHETHER ALLEGED TO ARISE BY OPERATION OF LAW, BY REASON OF CUSTOM OR USAGE
                IN THE TRADE, BY COURSE OF DEALING OR OTHERWISE, INCLUDING ANY AND ALL:
                (A) WARRANTIES OF MERCHANTABILITY; (B) WARRANTIES OF FITNESS OR
                SUITABILITY FOR ANY PURPOSE (WHETHER OR NOT DEMAND LOCAL KNOWS, HAS REASON
                TO KNOW, HAS BEEN ADVISED, OR IS OTHERWISE AWARE OF ANY SUCH PURPOSE); AND
                (C) WARRANTIES OF NONINFRINGEMENT OR CONDITION OF TITLE.
              </span>
              <span>
                8.5. <span className="underline">Third-Party Services Disclaimer</span>.
                The Services may be integrated with services provided by third parties as
                part of the functionality of the Services. Customer understands that
                Demand Local does not have control over third parties and that such third
                parties are not agents of Demand Local. CUSTOMER ACKNOWLEDGES AND AGREES
                THAT DEMAND LOCAL MAKES NO REPRESENTATION OR WARRANTY ABOUT, DOES NOT
                ENDORSE, AND WILL NOT BE LIABLE FOR ANY THIRD PARTY'S PRODUCTS OR SERVICES
                OR THE INFORMATION PROVIDED BY THIRD PARTIES, WHETHER THROUGH THE SERVICES
                OR OTHERWISE. Accordingly, Demand Local is not responsible for Customer's
                use of any third-party product or service or any harm or losses arising
                from or relating to Customer's use of any third-party products or
                services. Customer should contact the third party with any questions about
                their products and services. Demand Local hereby disclaims and Customer
                hereby discharges, waives and releases Demand Local and Demand Local's
                licensors and suppliers from any past, present, and future claims,
                liabilities, and damages, known or unknown, arising out of or relating to
                Customer's interactions with such third parties and their products and
                services.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">Indemnification Obligations</span>
              <span>
                9.1. <span className="underline">Customer Indemnity</span>. Customer, at
                its sole expense, will defend Demand Local and its affiliates, directors,
                officers, employees, and agents (“
                <span className="font-bold">Demand Local Indemnitees</span>”) from and
                against any third- party claims, suits, actions or proceedings (each a “
                <span className="font-bold">Claim</span>”) and indemnify Demand Local
                Indemnitees from any related damages, payments, deficiencies, fines,
                judgments, settlements, liabilities, losses, costs, and expenses
                (including, but not limited to, reasonable attorneys' fees, costs,
                penalties, interest and disbursements) (collectively, “
                <span className="font-bold">Losses</span>”) to the extent arising from or
                relating to (a) any negligence or willful misconduct by Customer or its
                affiliates, directors, officers, employees, or agents, or any other party
                acting on Customer's behalf (the “
                <span className="font-bold">Customer Indemnity Parties</span>”); (b) any
                alleged or actual breach of Customer's representations, warranties and
                obligations under this Agreement; (c) the use of the Platform by Customer
                Indemnity Parties, including without limitation any claim by Customer's
                employees or agents related to the use of the Platform by Customer
                Indemnity Parties; and (d) any violation of applicable Laws and Orders by
                Customer Indemnity Parties.
              </span>
              <span>
                9.2. <span className="underline">Demand Local Indemnity</span>. Demand
                Local, at its sole expense, will defend, indemnify, and hold harmless
                Customer from and against any Claims and indemnify Customer from any
                Losses to the extent arising from a claim that the Platform infringes or
                misappropriates any intellectual property rights of any third party. In
                the event of a Claim pursuant to this Section, Demand Local may, at Demand
                Local's option and expense (1) obtain for Customer the right to continue
                to exercise the rights granted to Customer under this Agreement; (2)
                substitute the allegedly infringing component for an equivalent
                non-infringing component; (3) modify the Platform to make it
                non-infringing; or (4) if (1), (2), or (3) is not obtainable on
                commercially reasonable terms, in Demand Local's sole discretion, Demand
                Local may terminate this Agreement, effective immediately, by written
                notice to Customer. Upon a termination of this Agreement pursuant to the
                preceding sentence, Customer must cease using the Platform and Demand
                Local will refund any pre-paid amount Customer paid to Demand Local for
                the Services for the then-current Monthly Term adjusted pro-rata for any
                period during such then-current Monthly Term when any of the Services were
                provided to Customer. Demand Local's indemnification obligations do not
                extend to the extent that Claims arise from or relate to: (i) any
                negligent or willful misconduct of Customer or any of Customer's
                employees, contractors and/or service providers (collectively, the “
                <span className="font-bold">Customer Parties</span>”); (ii) any
                combination of the Platform (or any portion thereof) by any of the
                Customer Parties with any equipment, software, data or any other
                materials, provided that no infringement, misappropriation, or other
                violation of third party rights would have occurred without such
                combination; (iii) any modification to the Platform by any of the Customer
                Parties or any third party at a Customer Party's request, provided that no
                infringement, misappropriation, or other violation of third party rights
                would have occurred without such modification; (iv) the use of the
                Platform by any of the Customer Parties in a manner contrary to the terms
                of this Agreement where the infringement would not have occurred but for
                such use; (v) the continued use of the Platform after Demand Local has
                provided substantially equivalent non-infringing software or service. In
                the interest of clarity, the Parties understand and agree that this
                Section is intended to supersede and replace Section X(a) of the Standard
                Terms.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">10. Limitation of Liability</span>
              <span>
                TO THE EXTENT PERMITTED BY APPLICABLE LAW, (A) NEITHER PARTY WILL BE
                LIABLE FOR ANY LOSS OF PROFITS OR ANY INDIRECT, SPECIAL, INCIDENTAL,
                RELIANCE OR CONSEQUENTIAL DAMAGES OF ANY KIND, REGARDLESS OF THE FORM OF
                ACTION, WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY
                OR OTHERWISE, EVEN IF INFORMED OF THE POSSIBILITY OF SUCH DAMAGES IN
                ADVANCE; AND (B) EXCEPT FOR CUSTOMER'S OBLIGATION TO PAY THE FEES, A
                BREACH OF SECTION 4 (CONFIDENTIALITY) OR SECTION 5 (INTELLECTUAL PROPERTY
                RIGHTS), AND CUSTOMER'S INDEMNIFICATION OBLIGATIONS AS SET FORTH IN
                SECTION 9, EACH PARTY'S AGGREGATE LIABILITY TO THE OTHER PARTY WILL NOT
                EXCEED THE FEES ACTUALLY PAID BY CUSTOMER TO DEMAND LOCAL DURING THE
                TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDEING THE EVENT WHICH GAVE RISE
                TO SUCH LIABILITY. IN THE INTEREST OF CLARITY, THE PARTIES UNDERSTAND AND
                AGREE THAT THIS SECTION IS INTENDED TO SUPERSEDE AND REPLACE SECTION XI OF
                THE STANDARD TERMS.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">
                11. Term, Termination, and Effect of Termination
              </span>
              <span>
                11.1. <span className="underline"> Term</span>. The Term of the Agreement
                will commence on the Effective Date and shall continue until terminated by
                either Party as provided herein (the “
                <span className="font-bold">Term</span>”).
              </span>
              <span>
                11.2. <span className="underline">Termination</span>. Notwithstanding
                Section 11.1, either Party may terminate this Agreement as follows: (a) if
                the other Party materially breaches this Agreement (including, without
                limitation, in the case of Customer, nonpayment of the Fees) and does not
                remedy such failure within thirty (30) days after its receipt of written
                notice of such breach (unless the breach is of a nature that is incapable
                of being cured, in which case the non-breaching Party may terminate this
                Agreement immediately upon written notice); (b) if the other Party
                terminates its business activities or becomes insolvent, admits in writing
                to inability to pay its debts as they mature, makes an assignment for the
                benefit of creditors, or becomes subject to direct control of a trustee,
                receiver or similar authority; (c) upon thirty (30) days' written notice
                to the other Party, either Party may terminate this Agreement for
                convenience; or (d) as otherwise expressly set forth in this Agreement. In
                addition, either Party may terminate a Campaign with written notice to the
                other Party (via registered mail or email) at least five (5) days before
                the expiration of the then-applicable calendar month.
              </span>
              <span>
                11.3. <span className="underline">Effect of Termination</span>. Upon any
                termination of this Agreement: (a) the License and any other rights
                granted to Customer under this Agreement with respect to the Platform and
                the Services will immediately cease, (b) Customer shall immediately pay to
                Demand Local all amounts due and payable up to and through the effective
                date of termination, (c) except for a termination by Customer pursuant to
                Section 11.2(a) or a termination by Demand Local pursuant to Section
                11.2(c), Demand Local shall have no obligation to refund any prepaid Fees,
                and (d) the Receiving Party will, at the option of the Disclosing Party,
                promptly return to the Disclosing Party or destroy all Confidential
                Information of Disclosing Party then in the Receiving Party's possession.
                Upon Demand Local's termination of this Agreement pursuant to Section
                11.2(c), Customer shall be entitled to a refund of Fees for any period for
                which Customer has already paid but will not receive Services.
                Notwithstanding any terms to the contrary in this Agreement, any provision
                of this Agreement that, by its nature and context, is intended to survive
                this Agreement (including, without limitation, Customer's obligation to
                pay any unpaid Fees and Sections 3 through 12, inclusive) will survive any
                termination of this Agreement.
              </span>
            </div>
            <div className="flex flex-col gap-2 border-b-2 border-gainsboro-gray pb-3">
              <span className="font-bold">12. General Provisions</span>
              <span>
                12.1. <span className="underline">Non-Solicitation</span>. For the Term
                and for a period of 2 years thereafter, neither Party will, directly or
                indirectly solicit or recruit any employee, consultant or sub-contractor
                of the other Party, or induce or attempt to induce such person/entity to
                terminate his/her employment or engagement with, or otherwise cease
                his/her relationship with the other Party.
              </span>
              <span>
                12.2. <span className="underline">Entire Agreement</span>. This Agreement,
                including all exhibits to this Agreement, all of which are incorporated
                herein by reference, sets forth the entire agreement and understanding of
                the Parties relating to the subject matter hereof, and supersedes all
                prior or contemporaneous agreements, proposals, negotiations,
                conversations, discussions and understandings, written or oral, with
                respect to such subject matter and all past dealing or industry custom.
              </span>
              <span>
                12.3. <span className="underline">Independent Contractors</span>. Neither
                Party will, for any purpose, be deemed to be an agent, franchisor,
                franchise, employee, representative, owner or partner of the other Party,
                and the relationship between the Parties will only be that of independent
                contractors. Neither Party will have any right or authority to assume or
                create any obligations or to make any representations or warranties on
                behalf of any other Party, whether express or implied, or to bind the
                other Party in any respect whatsoever.
              </span>
              <span>
                12.4. <span className="underline">Dispute Resolution</span>. The Parties
                agree to resolve any dispute, claim or controversy arising out of or
                relating to this Agreement according to the terms of this Section. First,
                the parties agree to attempt in good faith to resolve the dispute through
                informal resolution for a period of fifteen (15) calendar days from and
                after the date one Party provides the other Party with written notice of
                the dispute. Second, if the dispute is not resolved through informal
                resolution, the Parties agree to attempt in good faith to resolve the
                dispute through mediation administered by American Arbitration Association
                (“AAA”) in San Francisco, California, the costs of which shall be divided
                equally between the Parties. Third, if the dispute is not resolved through
                informal resolution and mediation, the Parties agree to participate in
                binding arbitration administered by AAA in San Francisco, California. If a
                virtual mediation or arbitration option is available to the Parties and
                permitted by the AAA, the Parties agree to participate in the virtual
                mediation or arbitration in accordance with this Section. The Parties
                agree that, in the event of arbitration (or in the event of a lawsuit if
                this arbitration clause is deemed invalid or does not apply to a given
                dispute) the prevailing Party shall be entitled to costs and fees
                (including reasonable attorneys' fees). Either Party may bring a lawsuit
                solely for injunctive relief without first engaging in the dispute
                resolution process described in this Section. In the event that the
                dispute resolution procedures in this Section are found not to apply to a
                given claim, or in the event of a claim for injunctive relief as specified
                in the previous sentence, the Parties agree that any judicial proceeding
                will be brought in the state or federal courts of San Francisco,
                California. Both Parties consent to the exclusive venue and personal
                jurisdiction set forth in this Section. ALL CLAIMS MUST BE BROUGHT IN THE
                PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN
                ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING, AND, UNLESS AGREED TO
                OTHERWISE BY THE PARTIES, THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE
                PERSON'S CLAIMS.
              </span>
              <span>
                12.5. <span className="underline">Governing Law</span>. The validity,
                interpretation, construction and performance of this Agreement, and all
                acts and transactions pursuant hereto and the rights and obligations of
                the Parties hereto shall be governed, construed and interpreted in
                accordance with the laws of the State of California, without giving effect
                to principles of conflicts of law.
              </span>
              <span>
                12.6. <span className="underline">Assignment</span>. Neither this
                Agreement nor any right or duty under this Agreement may be transferred,
                assigned or delegated by either Party, by operation of applicable Laws or
                otherwise, without the prior written consent of other Party, and any
                attempted transfer, assignment or delegation without such consent will be
                void and without effect. Notwithstanding the foregoing, either Party may
                assign its rights and obligations hereunder in connection with a merger,
                reorganization, consolidation, or sale of all or substantially all of its
                assets. Subject to the foregoing, this Agreement will be binding upon, and
                will inure to the benefit of, the Parties and their respective
                representatives, heirs, administrators, successors and permitted assigns.
              </span>
              <span>
                12.7. <span className="underline">Amendments and Waivers</span>. Demand
                Local reserves the right to change the terms of this Agreement from time
                to time, with or without notice to you. If you continue to use the
                Services, you consent to the new terms of the Agreement. Any changes to
                this Agreement will become effective on the “Effective Date” indicated
                above. If you continue to use the Services after such date, you consent to
                the new terms of the Agreement. No failure or delay (in whole or in part)
                on the part of a Party to exercise any right or remedy hereunder will
                operate as a waiver thereof or effect any other right or remedy. All
                rights and remedies hereunder are cumulative and are not exclusive of any
                other rights or remedies provided hereunder or by applicable Laws. The
                waiver of one breach or default or any delay in exercising any rights will
                not constitute a waiver of any subsequent breach or default.
              </span>
              <span>
                12.8. <span className="underline">Notices</span>. Any notice made pursuant
                to this Agreement will be in writing and will be deemed delivered on (a)
                the date of delivery if delivered personally, (b) five (5) calendar days
                (or upon written confirmed receipt) after mailing if duly deposited in
                registered or certified mail or express commercial carrier, or (c) one (1)
                calendar (or upon written confirmed receipt) after being sent by email,
                addressed to Customer or to Demand Local, as the case may be, at the
                address or email address shown on the signature page of this Agreement or
                to such other address or email address as may be hereafter designated by
                either Party.
              </span>
              <span>
                12.9. <span className="underline">Severibility</span>. If any provision of
                this Agreement is invalid, illegal, or incapable of being enforced by any
                rule of law or public policy, all other provisions of this Agreement will
                nonetheless remain in full force and effect. Upon such determination that
                any provision is invalid, illegal, or incapable of being enforced, the
                Parties will negotiate in good faith to modify this Agreement so as to
                effect the original intent of the Parties as closely as possible in an
                acceptable manner to the end that the transactions contemplated hereby are
                fulfilled.
              </span>
              <span>
                12.10 <span className="underline">Counterparts</span>. This Agreement may
                be executed: (a) in two or more counterparts, each of which will be deemed
                an original and all of which will together constitute the same instrument;
                and (b) by the Parties by exchange of signature pages by mail, facsimile
                or email (if email, signatures in Adobe PDF or similar format).
              </span>
              <span>
                12.11. <span className="underline">Publicity</span>. Customer consents to
                Demand Local's use of Customer's name and logo on Demand Local's website
                and other promotional material, identifying Customer as a customer of
                Demand Local and describing, without the use of any Confidential
                Information of Customer, Customer's use of the Services.
              </span>
              <span>
                12.12 <span className="underline">Force Majeure</span>. Neither Party will
                be responsible for any failure to perform or delay attributable in whole
                or in part to any cause beyond its reasonable control including, but not
                limited to, natural disasters (fire, storm, floods, earthquakes, etc.), a
                pandemic, acts of terrorism, civil disturbances, disruption of
                telecommunications, disruption of power or other essential services,
                interruption or termination of any third party services, labor
                disturbances, vandalism, cable cut, computer viruses or other similar
                occurrences, or any malicious or unlawful acts of any third party.
              </span>
              <span>
                12.13 <span className="underline">Construction</span>. This Agreement
                shall be deemed to be the product of all of the Parties, and no ambiguity
                shall be construed in favor of or against any one of the Parties.
              </span>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-end">
            <Button variant="rainbow" type="submit" disabled={isSubmitting}>
              {submitButtonLabel ?? 'Accept Terms'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Terms
